import {Alert, Button, createStyles, Group as MantineGroup, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import GroupTitleBar from 'notebook/notes/groups/GroupTitleBar';
import {
  NoteLinkedGroupResyncBannerContent,
  StructuredTableSpreadsheetProps,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {
  assertIsStructuredTableNotePart,
  isStructuredTableNotePart,
} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableFormOrView from 'notebook/structured-tables/StructuredTableFormOrView';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import withSpreadsheetEvaluation, {
  EnhancedProps,
  ExternalProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE, BLUE_POWDER_LIGHT, GRAY, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {maybeWithLoadingScreen} from 'util/Promises';

import {generateNoteLinkedObjectCollapseId} from '../util/generateNLCollapseId';
import {getNoteLinkedGroupAdapters} from './NoteLinkedGroupAdapters';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
  UnknownNoteLinkedGroup,
} from './NoteLinkedGroupConstants';
import {getContainedTableSearchResultProps} from './util/getContainedTableSearchResultProps';
import groupKindToIcon from './util/groupKindToIcon';
import maybeAsyncUpdateContainedNotePart from './util/maybeAsyncUpdateContainedNotePart';
import {
  getExternalPropsForSpreadsheetEval,
  makeTableItemPropsByName,
  makeTableItemPropsByNameForIsolatedEval,
} from './util/NoteLinkedGroupViewSpreadsheetProps';

/*
This component should show the name and the action functions for the Group
and then render each of the note linked objects contained within the group.
*/

export interface NoteLinkedGroupProps extends Partial<MediocreSearchResultProps> {
  notePartCid: string;
  group: UnknownNoteLinkedGroup;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  spreadsheetProps: StructuredTableSpreadsheetProps;
}

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
}

function GroupActionButtons<TGroupMetadata extends {}, TCreateGroupExtras extends {}>(
  props: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): JSX.Element {
  const actionFunctions = props.noteLinkedGroupAdapter.getActionFunctions();

  // When collapsed, only show the primary action function.
  return (
    <>
      {!props.isCollapsed &&
        actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>{actionFunction.renderToolbarButton(props)}</React.Fragment>
        ))}
      {!props.isCollapsed && actionFunctions.overflow?.length ? (
        <OverflowMenu
          options={actionFunctions.overflow
            .map((actionFunction) => actionFunction.getOverflowMenuItem(props))
            .filter((option) => option != null)}
        />
      ) : null}
      {actionFunctions.primary?.renderToolbarButton(props)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Resync banner (entry-level evaluation path)
// ---------------------------------------------------------------------------

const useResyncBannerStyles = createStyles((theme) => ({
  // Match the 6px padding that `formInsideGroup` (StructuredTableForm.tsx) applies around
  // each contained table inside the group, so the banner is inset to the same horizontal
  // gutter as the metadata and ingredients tables on either side of it.
  bannerWrapper: {
    padding: '6px',
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    width: '100%',
  },
}));

interface NoteLinkedGroupResyncBannerProps {
  content: NoteLinkedGroupResyncBannerContent;
  onApply: () => void;
}

/**
 * Export/print-PDF rendering of the resync banner. The PDF export pipeline renders React
 * server-side without Mantine's emotion styles (only compiled LESS/inline styles survive),
 * so the Mantine {@link Alert} version below would render unstyled. This variant uses inline
 * styles that always survive the export. It also omits the "apply" control, mirroring how
 * other interactive group chrome (e.g. GroupTitleBar action buttons) is hidden in read-only
 * export/print views.
 */
function NoteLinkedGroupResyncBannerForExport({
  content,
}: {
  content: NoteLinkedGroupResyncBannerContent;
}): JSX.Element {
  return (
    // Match the 6px padding that `formInsideGroup` (StructuredTableForm.tsx) applies around
    // each contained table, so the banner is inset to the same gutter as the tables.
    <div style={{padding: 6}}>
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          backgroundColor: BLUE_POWDER_LIGHT,
          borderRadius: 4,
          padding: '12px 16px',
        }}
        {...getTestProps({component: 'NoteLinkedGroupResyncBanner', element: 'exportBanner'})}
      >
        <BenchlingIcon icon="info" style={{color: BLUE, flex: 'none', marginTop: 2}} />
        <div>
          <div style={{fontWeight: 600, color: NEAR_BLACK}}>{content.title}</div>
          <div style={{color: GRAY_DARKER}}>{content.subtitle}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders the resync banner displayed inside a NoteLinkedGroup whose persisted state is
 * detectably out of sync with the latest evaluator output (see
 * {@link NoteLinkedGroupAdapter.isGroupResyncRequired}). The banner is slotted in between
 * the group's contained tables; the kind-specific copy comes from
 * {@link NoteLinkedGroupAdapter.getResyncBannerContent} so this component stays generic.
 */
function NoteLinkedGroupResyncBanner(props: NoteLinkedGroupResyncBannerProps): JSX.Element {
  const {classes} = useResyncBannerStyles();
  const {content, onApply} = props;
  const {isExportView} = EditorPropertiesContext.useContext();

  // Mantine (emotion) styles are not applied in the PDF export, so render an inline-styled
  // banner there instead of the unstyled Mantine Alert.
  if (isExportView) {
    return <NoteLinkedGroupResyncBannerForExport content={content} />;
  }

  return (
    <div className={classes.bannerWrapper}>
      <Alert
        color="blue"
        variant="light"
        icon={<BenchlingIcon icon="info" />}
        role="alert"
        {...getTestProps({component: 'NoteLinkedGroupResyncBanner'})}
      >
        <MantineGroup className={classes.bannerContent} noWrap={true}>
          <div>
            <Text weight={600}>{content.title}</Text>
            <Text>{content.subtitle}</Text>
          </div>
          <Button
            onClick={onApply}
            {...getTestProps({component: 'NoteLinkedGroupResyncBanner', element: 'applyButton'})}
          >
            {content.buttonText}
          </Button>
        </MantineGroup>
      </Alert>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Isolated evaluation path (groups that do NOT opt in to entry-level eval)
// ---------------------------------------------------------------------------

interface ActionFunctionContextProps {
  actionFunctionContext: ActionFunctionContext;
}
export type NoteLinkedGroupIsolatedViewProps = NoteLinkedGroupProps &
  ExternalProps<NotePart | UnknownNoteLinkedGroup, Note | UnknownNoteLinkedGroup> &
  EnhancedProps &
  ActionFunctionContextProps & {
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
  };

// withSpreadsheetEvaluation triggers "Type instantiation is excessively deep and possibly infinite"
// when used with functional components, so this must remain a class component.
// eslint-disable-next-line react/prefer-stateless-function
class NoteLinkedGroupIsolatedView<
  TGroupMetadata extends {},
  TCreateGroupExtras extends {},
> extends React.Component<NoteLinkedGroupIsolatedViewProps & ConnectedProps> {
  override render(): JSX.Element {
    const {props} = this;
    const {
      group,
      editor,
      editorProps,
      notePartCid,
      selectorsById,
      actionFunctionContext,
      isCollapsed,
      onToggleCollapsed,
    } = props;
    const adapter = getNoteLinkedGroupAdapters()[group.kind];
    const handleContainedNotePartContentUpdate = async (
      containedNotePartIndex: number,
      content: object
    ): Promise<void> => {
      const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
      const updatedContainedNotePart = editor.getItemWithUpdatedContent(oldContainedNotePart, content);
      await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart);
    };
    const handleDeleteGroup = (): void => {
      const parentIndex = editor.getIndex(notePartCid);
      assert(parentIndex !== -1, `Could not find note part with cid ${notePartCid}`);
      editor.itemDeleteHandler(parentIndex);
    };
    const {evaluatedTableMap, tableItemPropsByName} = makeTableItemPropsByNameForIsolatedEval(props);
    const actionFunctionParameters: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras> = {
      entryOrTemplateId: editorProps.fileId,
      noteLinkedGroup: group as NoteLinkedGroup<TGroupMetadata>,
      noteLinkedGroupAdapter: adapter as NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras>,
      locked: editorProps.locked,
      isCollapsed,
      evaluatedTableMap,
      actionFunctionContext,
      getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
      getIdFromCidFn: editorProps.getIdFromCid,
      updateGroup: (noteLinkedGroup: NoteLinkedGroup<{}>) => {
        editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
      },
      insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => {
        editor.props.registerObjects(cidIdMap);
        editor.itemInsertHandler(editor.getIndex(notePartCid), noteParts);
      },
      selectorsById,
      groupResyncRequired: false,
    };
    // Print preview should display all groups expanded.
    const isExpanded = !isCollapsed || props.editorProps.viewer;
    const printableContainerClass = props.editorProps.viewer ? 'printable-container-scaleForPrint' : '';
    return (
      <SelectableNotePart indentation={0} className={printableContainerClass}>
        <div
          className={classNames(css(styles.groupWrapper), 'notebook-export--group-wrapper')}
          {...getTestProps({component: 'NoteLinkedGroupView', element: 'groupWrapper'})}
        >
          <GroupTitleBar
            groupTypeLabel={adapter.getDisplayName(group)}
            groupName={group.name}
            icon={groupKindToIcon(group.kind)}
            rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
            isCollapsed={isCollapsed}
            onToggleCollapsed={onToggleCollapsed}
            viewer={props.editorProps.viewer}
          />
          {isExpanded && (
            <div
              className={classNames(css(styles.partsWrapper), 'notebook-export--parts-wrapper')}
              {...getTestProps({component: 'NoteLinkedGroupView', element: 'partsWrapper'})}
            >
              {group.containedNoteParts.map((containedNotePart, index) => {
                // For now, NoteLinkedGroup only supports structured tables, which may be relaxed in the future.
                assertIsStructuredTableNotePart(containedNotePart);
                const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
                return (
                  <StructuredTableFormOrView
                    key={containedNotePart.cid}
                    editor={editor}
                    // HACK: withSpreadsheetEvaluation HOF injects a handful of props that override the spreadsheet
                    // behavior given by editorProps.
                    editorProps={{...editorProps, ...props}}
                    index={index}
                    notePart={containedNotePart}
                    onUpdateContent={handleContainedNotePartContentUpdate}
                    isInGroup={true}
                    deleteHandler={handleDeleteGroup}
                    itemProps={tableItemPropsByName[containedNotePart.item.name]}
                    adapter={tableAdapter}
                    overrideDefaultTableProps={{
                      disableMaximizedTables: true,
                      isCollapsible: false,
                    }}
                    {...getContainedTableSearchResultProps(props, containedNotePart.cid)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </SelectableNotePart>
    );
  }
}

const NoteLinkedGroupIsolatedViewWithSpreadsheet = withSpreadsheetEvaluation<
  NoteLinkedGroupProps &
    ExternalProps<NotePart | UnknownNoteLinkedGroup, Note | UnknownNoteLinkedGroup> &
    ActionFunctionContextProps & {
      isCollapsed: boolean;
      onToggleCollapsed: () => void;
    },
  NotePart | UnknownNoteLinkedGroup,
  Note | UnknownNoteLinkedGroup
>(NoteLinkedGroupIsolatedView);

// ---------------------------------------------------------------------------
// Entry-level evaluation path (groups that opt in to entry-level eval)
// This component duplicates some structure from NoteLinkedGroupIsolatedView above.
// The duplication is temporary: once all NLG types migrate to entry-level eval
// and the feature gate is removed, NoteLinkedGroupIsolatedView and its
// withSpreadsheetEvaluation wrapper will be deleted.
// ---------------------------------------------------------------------------

export type NoteLinkedGroupEntryLevelViewProps = NoteLinkedGroupProps &
  ConnectedProps &
  ActionFunctionContextProps & {
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
  };

function NoteLinkedGroupEntryLevelView(props: NoteLinkedGroupEntryLevelViewProps): JSX.Element {
  const {
    group,
    editor,
    editorProps,
    notePartCid,
    selectorsById,
    spreadsheetProps,
    actionFunctionContext,
    isCollapsed,
    onToggleCollapsed,
  } = props;
  const adapter = getNoteLinkedGroupAdapters()[group.kind];

  const handleContainedNotePartContentUpdate = useCallback(
    async (
      containedNotePartIndex: number,
      content: object,
      _selectors?: never,
      tableOp?: TableOpDiffMap
    ): Promise<void> => {
      const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
      const updatedContainedNotePart = editor.getItemWithUpdatedContent(oldContainedNotePart, content);
      await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart, tableOp);
    },
    [group, editor, notePartCid]
  );

  const handleDeleteGroup = useCallback((): void => {
    const parentIndex = editor.getIndex(notePartCid);
    assert(parentIndex !== -1, `Could not find note part with cid ${notePartCid}`);
    editor.itemDeleteHandler(parentIndex);
  }, [editor, notePartCid]);

  const {cellColors, editingTable, computedTables, showExcessLabels} = editor.props;
  const {evaluatedTableMap, tableItemPropsByName} = useMemo(
    () =>
      makeTableItemPropsByName(
        group,
        editor,
        spreadsheetProps,
        cellColors,
        editingTable,
        computedTables,
        showExcessLabels
      ),
    [group, editor, spreadsheetProps, cellColors, editingTable, computedTables, showExcessLabels]
  );

  const groupResyncRequired = adapter.isGroupResyncRequired(group, evaluatedTableMap);

  const onApplyResync = useCallback((): void => {
    const currentAdapter = getNoteLinkedGroupAdapters()[group.kind];
    const maybeUpdatedGroup = maybeWithLoadingScreen(
      currentAdapter.resyncGroupComponentsAsync(group, evaluatedTableMap),
      'Updating...'
    );
    const dispatchUpdate = (updatedGroup: UnknownNoteLinkedGroup): void => {
      if (updatedGroup === group) {
        return;
      }
      const index = editor.getIndex(notePartCid);
      if (index === -1) {
        return;
      }
      editor.itemUpdateHandler(index, {item: updatedGroup});
    };
    if (maybeUpdatedGroup instanceof Promise) {
      maybeUpdatedGroup.then(dispatchUpdate).catch(() => {
        // Errors are surfaced by the underlying reducer (e.g. asserts on missing parent
        // mixture access); we just need to ensure the loading screen is dismissed, which
        // maybeWithLoadingScreen handles in its `finally` block.
      });
    } else {
      dispatchUpdate(maybeUpdatedGroup);
    }
  }, [editor, group, evaluatedTableMap, notePartCid]);

  const actionFunctionParameters: GroupActionFunctionParameters<{}, {}> = {
    entryOrTemplateId: editorProps.fileId,
    noteLinkedGroup: group as NoteLinkedGroup<{}>,
    noteLinkedGroupAdapter: adapter as NoteLinkedGroupAdapter<{}, {}>,
    locked: editorProps.locked,
    isCollapsed,
    evaluatedTableMap,
    actionFunctionContext,
    getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
    getIdFromCidFn: editorProps.getIdFromCid,
    updateGroup: (noteLinkedGroup: NoteLinkedGroup<{}>) => {
      editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
    },
    insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => {
      editor.props.registerObjects(cidIdMap);
      editor.itemInsertHandler(editor.getIndex(notePartCid), noteParts);
    },
    selectorsById,
    groupResyncRequired,
  };
  // Print preview should display all groups expanded.
  const isExpanded = !isCollapsed || editorProps.viewer;
  const printableContainerClass = editorProps.viewer ? 'printable-container-scaleForPrint' : '';
  return (
    <SelectableNotePart indentation={0} className={printableContainerClass}>
      <div
        className={classNames(css(styles.groupWrapper), 'notebook-export--group-wrapper')}
        {...getTestProps({component: 'NoteLinkedGroupView', element: 'groupWrapper'})}
      >
        <GroupTitleBar
          groupTypeLabel={adapter.getDisplayName(group)}
          groupName={group.name}
          icon={groupKindToIcon(group.kind)}
          rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
          isCollapsed={isCollapsed}
          onToggleCollapsed={onToggleCollapsed}
          viewer={editorProps.viewer}
        />
        {isExpanded && (
          <div
            role="presentation"
            className={classNames(css(styles.partsWrapper), 'notebook-export--parts-wrapper')}
            {...getTestProps({component: 'NoteLinkedGroupView', element: 'partsWrapper'})}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onMouseOver={(e) => {
              e.stopPropagation();
            }}
          >
            {group.containedNoteParts.flatMap((containedNotePart, index) => {
              assertIsStructuredTableNotePart(containedNotePart);
              const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
              const renderedTable = (
                <StructuredTableFormOrView
                  key={containedNotePart.cid}
                  editor={editor}
                  editorProps={editorProps}
                  index={index}
                  notePart={containedNotePart}
                  onUpdateContent={handleContainedNotePartContentUpdate}
                  isInGroup={true}
                  deleteHandler={handleDeleteGroup}
                  itemProps={tableItemPropsByName[containedNotePart.item.name]}
                  adapter={tableAdapter}
                  overrideDefaultTableProps={{
                    disableMaximizedTables: true,
                    isCollapsible: false,
                  }}
                  {...getContainedTableSearchResultProps(props, containedNotePart.cid)}
                />
              );
              // Render the resync banner directly under the metadata table (first contained
              // note part). Today only mixture groups can require resync, and their first
              // contained note part is always the metadata table; if another group kind ever
              // opts in this position will need to be looked up from the adapter.
              if (groupResyncRequired && index === 0) {
                return [
                  renderedTable,
                  <NoteLinkedGroupResyncBanner
                    key={`${containedNotePart.cid}-resync-banner`}
                    content={adapter.getResyncBannerContent()}
                    onApply={onApplyResync}
                  />,
                ];
              }
              return [renderedTable];
            })}
          </div>
        )}
      </div>
    </SelectableNotePart>
  );
}

// ---------------------------------------------------------------------------
// Routing component
// ---------------------------------------------------------------------------

function UnconnectedNoteLinkedGroupView(props: NoteLinkedGroupProps & ConnectedProps): JSX.Element {
  const adapter = getNoteLinkedGroupAdapters()[props.group.kind];
  const actionFunctionContext = useActionFunctionContext();

  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(
    generateNoteLinkedObjectCollapseId(props.editorProps.fileId, props.group.provenanceUuid)
  );

  if (adapter.supportsEntryLevelEvaluation()) {
    return (
      <NoteLinkedGroupEntryLevelView
        {...props}
        actionFunctionContext={actionFunctionContext}
        isCollapsed={isCollapsed}
        onToggleCollapsed={onToggleCollapsed}
      />
    );
  }

  const externalProps = getExternalPropsForSpreadsheetEval(
    props.editorProps,
    props.group,
    props.editor,
    props.notePartCid
  );
  return (
    <NoteLinkedGroupIsolatedViewWithSpreadsheet
      {...props}
      {...externalProps}
      actionFunctionContext={actionFunctionContext}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
}

class NoteLinkedGroupViewConnector extends DataConnector<NoteLinkedGroupProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps: () => ConnectedProps;

  constructor(props: NoteLinkedGroupProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  getAllSchemaFields(group: UnknownNoteLinkedGroup): Array<SchemaField> {
    return group.containedNoteParts.flatMap((notePart) => {
      if (!isStructuredTableNotePart(notePart)) {
        return [];
      }
      const structuredTable = notePart.item as UnknownStructuredTable;
      return structuredTable.schemas.flatMap((schema) => schema.allFields);
    });
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const fields = this.getAllSchemaFields(this.props.group);
    const selectorIds = getSelectorIdsFromFields(fields);
    if (selectorIds.length === 0) {
      return {
        selectorsById: {},
      };
    }
    const selectorsById = this.getOrFetchMultipleOptionalFull(
      ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
      selectorIds
    );
    return {selectorsById};
  };
}

// Only trigger a re-render due to a redux state change if the ConnectedProps change.
export const NoteLinkedGroupView = NoteLinkedGroupViewConnector.connect(UnconnectedNoteLinkedGroupView, {
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});

export default NoteLinkedGroupView;

const styles = StyleSheet.create({
  groupWrapper: {
    backgroundColor: 'white',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
  partsWrapper: {
    // The contained noteParts need to be stretched outward by a pixel. This ensures that any
    // table borders coincide with the group border, rather than creating a two-pixel border.
    margin: '0 -1px',
  },
});
